import {useMemo} from 'react'

import {useListMembersQuery} from '#/state/queries/list-members'
import {AvatarBubbles} from '#/components/AvatarBubbles'
import type * as bsky from '#/types/bsky'

const MAX_BUBBLES = 4
const MEMBERS_LIMIT = 10

function getListUri(
  starterPack: bsky.starterPack.AnyStarterPackView,
): string | undefined {
  if ('list' in starterPack && starterPack.list?.uri) {
    return starterPack.list.uri
  }
  const list = (starterPack.record as {list?: string}).list
  return typeof list === 'string' ? list : undefined
}

export function EntourageAvatars({
  starterPack,
  profiles,
  size = 40,
  animate = false,
}: {
  starterPack?: bsky.starterPack.AnyStarterPackView
  profiles?: bsky.profile.AnyProfileView[]
  size?: number
  animate?: boolean
}) {
  const sampleProfiles = useMemo(() => {
    if (!starterPack || !('listItemsSample' in starterPack)) return null
    const sample = starterPack.listItemsSample
    if (!sample || sample.length === 0) return null
    return [starterPack.creator, ...sample.map(item => item.subject)]
  }, [starterPack])

  // Post embeds and notifications only carry the basic starter pack view, and
  // some deployments don't populate `listItemsSample` on the full view either.
  // Fetch members from the list itself so the cluster is always populated.
  const listUri = useMemo(() => {
    if (sampleProfiles) return undefined
    return starterPack ? getListUri(starterPack) : undefined
  }, [sampleProfiles, starterPack])

  const {data: listMembers} = useListMembersQuery(listUri, MEMBERS_LIMIT)

  const computedProfiles = useMemo(() => {
    const source = sampleProfiles
      ? sampleProfiles
      : listMembers
        ? [
            starterPack?.creator,
            ...(listMembers.pages?.[0]?.items ?? []).map(item => item.subject),
          ]
        : [starterPack?.creator, ...(profiles ?? [])]

    const seen = new Set<string>()
    const deduped: bsky.profile.AnyProfileView[] = []
    for (const profile of source) {
      if (!profile || seen.has(profile.did)) continue
      seen.add(profile.did)
      deduped.push(profile)
    }
    return deduped.slice(0, MAX_BUBBLES)
  }, [sampleProfiles, listMembers, starterPack, profiles])

  return (
    <AvatarBubbles profiles={computedProfiles} size={size} animate={animate} includeSelf />
  )
}
